import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { useOpenRideInNewTab, useRidesNavigation } from "@ryde/redux";
import {
  IRichLogData,
  Log,
  NO_VALUE_DISPLAY,
  RydeAppUnit,
  useDrawers,
} from "@ryde/shared";

import {
  IRichLogsFilter,
  RICH_LOGS_PAGE_SIZE,
  RichLogFormFields,
} from "../rich-logs.models";
import { useRichLogsFilteredRecords } from "./use-rich-logs.filtered-records";

interface IUseRichLogsContentProps {
  logData: IRichLogData | undefined;
}

export const useRichLogsContent = ({ logData }: IUseRichLogsContentProps) => {
  const {
    openEditFormDrawer,
    openRideEditFormDrawer,
    openStationRouteEditFormDrawer,
    openCourseEditFormDrawer,
  } = useDrawers();
  const { watch, setValue } = useFormContext<IRichLogsFilter>();
  const { isRidesEditRoute } = useRidesNavigation();
  const { openRideInNewTab } = useOpenRideInNewTab();

  const handleOpenDrawer = useEvent(
    ({ entity, id }: { entity: RydeAppUnit; id: number }) => {
      openEditFormDrawer({ entity, id });
    },
  );

  const handleLogItemClick = useEvent((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const linkElement =
      target.closest("a") ?? target.closest("button[data-id][data-entity]");

    if (linkElement) {
      const id = linkElement.getAttribute("data-id");
      const entity = linkElement.getAttribute("data-entity") as RydeAppUnit;
      const parsedId = Number.isFinite(Number(id)) ? Number(id) : undefined;

      if (!parsedId || !entity || entity === "unknown") {
        Log.warn(
          `Invalid link for entity ${entity || NO_VALUE_DISPLAY} with id ${parsedId || NO_VALUE_DISPLAY} `,
        );
        return;
      }

      if (entity === "ride") {
        if (isRidesEditRoute) {
          openRideInNewTab(parsedId);
        } else {
          openRideEditFormDrawer({
            id: parsedId,
            idCopyFrom: undefined,
          });
        }
      } else if (entity === "stationRoute") {
        openStationRouteEditFormDrawer({
          id: parsedId,
          idCopyFrom: undefined,
        });
      } else if (entity === "course") {
        openCourseEditFormDrawer({
          id: parsedId,
          idCopyFrom: undefined,
        });
      } else {
        handleOpenDrawer({ entity, id: parsedId });
      }
    }
  });

  const searchText = watch(RichLogFormFields.searchText);
  const currentPage = watch(RichLogFormFields.page) || 1;

  const filteredAndSortedLogs = useRichLogsFilteredRecords({ logData });

  const totalPages = Math.ceil(
    filteredAndSortedLogs.length / RICH_LOGS_PAGE_SIZE,
  );
  const startIndex = (currentPage - 1) * RICH_LOGS_PAGE_SIZE;
  const endIndex = startIndex + RICH_LOGS_PAGE_SIZE;
  const paginatedLogs = filteredAndSortedLogs.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setValue(RichLogFormFields.page, 1);
    }
  }, [currentPage, totalPages, setValue]);

  return {
    searchText,
    currentPage,
    filteredAndSortedLogs,
    totalPages,
    startIndex,
    endIndex,
    paginatedLogs,
    handleLogItemClick,
  };
};
